/**
 * SystemsWorkConfig — typed replacement for deploy/public/json/systems.json.
 *
 * The SYSTEMS department work carousel: feel settings plus two project tiles
 * and their floating particle layers. See ScreensWorkConfig for the field
 * conventions.
 */
import { TitleLineType, type ProjectConfig } from 'app/model/ProjectConfig';
import { HeaderType } from 'app/views/project/project.types';
import type { WorkConfig } from 'app/model/WorkConfig';

/**
 * Immersive Gamebox — interactive game rooms. Grid/box particles around the
 * tile.
 */
export const immersiveGamebox: ProjectConfig = {
	order: 1,
	slug: 'immersive-gamebox',
	image: 'igb.jpg',
	tint: '0xe169ff',
	particles: [
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: -0.025,
			y: 0.25,
			z: 5,
			alpha: 0.1,
			depth: 1,
			scale: 0.6,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: -0.035,
			y: 0.7,
			z: 5,
			alpha: 0.1,
			depth: 1,
			scale: 0.45,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: 0.75,
			y: 1.03,
			z: 7,
			depth: 1,
			alpha: 0.07,
			scale: 1.3,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: 0.8,
			y: 1.0,
			z: 5,
			depth: 1,
			alpha: 0.13,
			scale: 0.9,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: 0.99,
			y: 0.2,
			z: 5,
			depth: 1,
			alpha: 0.13,
			scale: 0.9,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-large
		{
			texture: 'rpo-dust-large.png',
			x: 1,
			y: 1,
			z: 5,
			depth: 1,
			alpha: 0.03,
			scale: 1.4,
			retainInCaseStudy: true,
			autoAlpha: true,
			autoScale: true
		},
		// rpo-dust-1
		{
			texture: 'rpo-dust-1.png',
			x: 0.08,
			y: 0.46,
			z: 5,
			depth: 1,
			scale: 0.9,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		},
		// rpo-dust-2
		{
			texture: 'rpo-dust-2.png',
			x: 0.4,
			y: -0.02,
			z: 4.4,
			depth: 1,
			scale: 0.9,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		},
		// rpo-dust-3
		{
			texture: 'rpo-dust-3.png',
			x: -0.07,
			y: 0.38,
			z: 4.8,
			depth: 1,
			scale: 0.8,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		},
		// rpo-dust-4
		{
			texture: 'rpo-dust-4.png',
			x: 0.2,
			y: 0.75,
			z: 3,
			depth: 1,
			scale: 0.9,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		},
		// rpo-dust-5
		{
			texture: 'rpo-dust-5.png',
			x: 1.02,
			y: 0.05,
			z: 2,
			depth: 1,
			scale: 0.9,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		},
		// rpo-dust-6
		{
			texture: 'rpo-dust-6.png',
			x: 0.95,
			y: 0.76,
			z: 6,
			depth: 1,
			scale: 0.9,
			retainInCaseStudy: false,
			autoAlpha: true,
			autoRotate: true
		}
	]
};

/**
 * March for Giants (systems edition — slug 'march-for-giants-s', same tile art
 * as the screens entry). Video header, double title line.
 */
export const marchForGiantsS: ProjectConfig = {
	order: 2,
	slug: 'march-for-giants-s',
	image: 'm4g.jpg',
	headerType: HeaderType.VIDEO,
	tint: '0xe169ff',
	titleLineType: TitleLineType.DOUBLE,
	particles: [
		// m4g-taxi — hidden in case study
		{
			texture: 'm4g-taxi.png',
			x: 0.2,
			y: 0.97,
			z: 3,
			depth: -2,
			scale: 0.8,
			retainInCaseStudy: true,
			projectY: 0.97,
			caseStudyHide: true
		},
		// m4g-sign — hidden in case study
		{
			texture: 'm4g-sign.png',
			x: 0.13,
			y: 0.5,
			z: 5,
			depth: -2,
			scale: 0.8,
			retainInCaseStudy: true,
			projectY: 0.56,
			mobileProjectY: 0.69,
			mobileProjectX: 0.05,
			caseStudyHide: true
		},
		// m4g-taxis — hidden in case study
		{
			texture: 'm4g-taxis.png',
			x: 0.84,
			y: 0.95,
			z: 3.5,
			depth: -2,
			scale: 0.8,
			retainInCaseStudy: true,
			projectY: 0.93,
			caseStudyHide: true
		},
		// m4g-b1
		{
			texture: 'm4g-b1.png',
			x: 0.2,
			y: 1.01,
			z: 2,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b2
		{
			texture: 'm4g-b2.png',
			x: 0.0,
			y: 0.8,
			z: 3,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b3
		{
			texture: 'm4g-b3.png',
			x: -0.04,
			y: 0.2,
			z: 9,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b4
		{
			texture: 'm4g-b4.png',
			x: 0.18,
			y: -0.02,
			z: 4.5,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b5
		{
			texture: 'm4g-b5.png',
			x: 0.48,
			y: -0.03,
			z: 2,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b6
		{
			texture: 'm4g-b6.png',
			x: 0.75,
			y: -0.04,
			z: 5,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b7
		{
			texture: 'm4g-b7.png',
			x: 1.04,
			y: 0.1,
			z: 6,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true
		},
		// m4g-b8
		{
			texture: 'm4g-b8.png',
			x: 0.95,
			y: 0.85,
			z: 9,
			scale: 0.8,
			depth: 1,
			autoScale: true,
			autoRotate: true,
			autoAlpha: true,
			retainInCaseStudy: true,
			projectX: 1.05
		}
	]
};

/**
 * The assembled department config, in the ORIGINAL JSON array order.
 * WorkController.setJSON filters (`active !== false`) and sorts by `order`
 * before use, but the source order is preserved here for parity.
 */
export const SystemsWorkConfig: WorkConfig = {
	dragEasePower: 2, //Ease power while dragging
	easePower: 18, //Ease power while settling
	velocityFactor: 2.5, //Multiplies timeline movement into the drag/slide rotation effect
	velocityFriction: 0.9, //Per-frame falloff pulling the rotation back to 0
	rotationFactor: 45, //Base level of rotation (degrees)
	maxRotation: 35, //Max rotation allowed either way (can exceed base)
	dragToMoveRatio: 1, //Drag distance -> panel movement ratio (1 = 1:1)
	projects: [immersiveGamebox, marchForGiantsS]
};
